import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import getCurrentDpiLevels from 'editor/src/store/design/selector/getCurrentDpiLevels';
import { getMediaImageDPI } from 'editor/src/store/design/selector/getMediaElementDpi';
import getSharedTextFieldCapsSelector from 'editor/src/store/design/selector/getSharedTextFieldCapsSelector';
import getSpread from 'editor/src/store/design/selector/getSpread';
import getSpreadGroups from 'editor/src/store/design/selector/getSpreadGroups';
import { ImageManipulationPluginTypes, MediaElement, PrintTechnology } from 'editor/src/store/design/types';
import getContentAreaFromSpread from 'editor/src/store/design/util/getContentAreaFromSpread';
import { getOperativeCharCap } from 'editor/src/store/design/util/sharedTextFields';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import { WarningLevel, WarningType } from 'editor/src/store/editorModules/warnings/types';
import getDpiWarning from 'editor/src/store/editorModules/warnings/utils/getDpiWarning';
import getNotVisibleWarning from 'editor/src/store/editorModules/warnings/utils/getNotVisibleWarning';
import getOutOrRangeWarning from 'editor/src/store/editorModules/warnings/utils/getOutOrRangeWarning';
import getOutsideSheetWarning from 'editor/src/store/editorModules/warnings/utils/getOutsideSheetWarning';
import getImageById from 'editor/src/store/gallery/selector/getImageById';
import { MimeType } from 'editor/src/store/gallery/types';
import { useSelector, useStore } from 'editor/src/store/hooks';
import getHostSetting from 'editor/src/store/hostSettings/selector/getHostSetting';
import { getImageManipulationPluginData } from 'editor/src/store/plugins/utils/imageManipulationUtils';
import shouldDigitizeSpread from 'editor/src/store/utils/shouldDigitizeSpread';
import { isEmbedded } from 'editor/src/store/watchers/useDisplayMode';

import { canShowGoodDPI, getDPIWarningLabel, getDPIWarningTooltip, isImageAdjusted } from 'editor/src/util/dpiWarning';

import { getSpreadDisplay } from 'editor/src/component/EditorArea/useSpreadDisplay';

function useElementWarnings(element: MediaElement | undefined) {
  const { t } = useTranslation();
  const galleryImage = useSelector((state) =>
    element?.type === 'image' ? getImageById(state, element.imageId) : undefined,
  );
  const store = useStore();
  const limits = useSelector(getCurrentDpiLevels);
  const spreadIndex = useSelector(getCurrentSpreadIndex);
  const enabledWarnings = useSelector((state) => state.hostSettings.enabledWarnings);
  const hiddenWarnings = useSelector((state) => state.hostSettings.hiddenWarnings);
  // The group's cap rather than the layer's own wherever the group's value
  // reaches it: the strictest in the group is the limit the buyer actually
  // meets, so reading `extra.maxCharCount` would warn at a limit that is not
  // the operative one and stay quiet at the one that is.
  const charCap = useSelector((state) =>
    element?.type === 'text' ? getOperativeCharCap(element, getSharedTextFieldCapsSelector(state)) : undefined,
  );
  // GAPI-53417: the gang sheet block is the off-sheet warning's only opt-in
  const gangSheet = useSelector((state) => state.hostSettings.gangSheet);
  const { warnings, level, tooltipLabel, text } = useMemo(() => {
    const warnings = [];
    let level: WarningLevel | undefined;
    let tooltipLabel: string | undefined;
    let text: string | undefined;

    const state = store.getState();
    const spread = getSpread(state, spreadIndex);
    const enableImageAutoEnhance = getHostSetting(state, 'enableImageAutoEnhance');
    if (element && spread) {
      const dpiWarning =
        !hiddenWarnings.LowDPI &&
        enabledWarnings.LowDPI &&
        element.type === 'image' &&
        !element.sample &&
        galleryImage &&
        limits
          ? getDpiWarning(element, spreadIndex, galleryImage, limits, spread)
          : undefined;
      if (dpiWarning) {
        warnings.push(dpiWarning);
        level = dpiWarning.level;
        const dpi = shouldDigitizeSpread(spread) ? undefined : dpiWarning.dpi;
        tooltipLabel = isEmbedded
          ? t('Low quality image message')
          : getDPIWarningTooltip(dpi, dpiWarning.level, t, enableImageAutoEnhance, isImageAdjusted(element));
        text = isEmbedded ? t('Low quality image') : getDPIWarningLabel(dpi, t);
      }

      const spreadGroups = getSpreadGroups(state);
      const spreadDisplay = getSpreadDisplay(spread, spreadIndex, spreadGroups);

      const outOfRangeWarning =
        !hiddenWarnings.OutOfRange && enabledWarnings.OutOfRange
          ? getOutOrRangeWarning(element, spreadIndex, spreadDisplay.spreadWidth, spreadDisplay.spreadHeight)
          : undefined;
      if (outOfRangeWarning) {
        warnings.push(outOfRangeWarning);
        level ??= WarningLevel.Medium;
        tooltipLabel ||= t('Element has incorrect dimensions');
      }

      const notVisibleWarning =
        !hiddenWarnings.NotVisible &&
        enabledWarnings.NotVisible &&
        getNotVisibleWarning(
          element,
          spreadIndex,
          spreadDisplay.spreadCoords,
          spreadDisplay.spreadWidth,
          spreadDisplay.spreadHeight,
        );
      if (notVisibleWarning) {
        warnings.push(notVisibleWarning);
        level ??= WarningLevel.Medium;
        tooltipLabel ||= t('Element outside of the print area');
      }

      const gangSheetArea = gangSheet ? getContentAreaFromSpread(spread) : undefined;
      const outsideSheetWarning = gangSheetArea && getOutsideSheetWarning(element, spreadIndex, gangSheetArea);
      if (outsideSheetWarning) {
        warnings.push(outsideSheetWarning);
        level = WarningLevel.High;
        // TODO: Lokalino
        tooltipLabel = 'Artwork is off the sheet';
      }

      if (warnings.length > 1) {
        tooltipLabel += ` (+${warnings.length - 1})`;
      } else if (
        enabledWarnings.LowDPI &&
        !hiddenWarnings.LowDPI &&
        warnings.length === 0 &&
        element.type === 'image' &&
        !element.sample &&
        limits &&
        galleryImage &&
        canShowGoodDPI(galleryImage)
      ) {
        const dpi = shouldDigitizeSpread(spread) ? undefined : getMediaImageDPI(element, galleryImage);
        tooltipLabel = getDPIWarningTooltip(dpi, undefined, t, enableImageAutoEnhance, isImageAdjusted(element));
        text = getDPIWarningLabel(dpi, t);
      }

      if (
        !hiddenWarnings.MaxCharCountReached &&
        enabledWarnings.MaxCharCountReached !== false &&
        element.type === 'text' &&
        charCap !== undefined &&
        element.extra.text.length >= charCap
      ) {
        warnings.push({
          type: WarningType.MaxCharCountReached,
          uuid: element.uuid,
          spreadIndex,
          level: WarningLevel.Medium,
        });
        level ??= WarningLevel.Medium;
        tooltipLabel ||= t('editor-warning-max-char-count-reached');
        text ||= t('editor-warning-max-char-count-reached');
      }

      if (
        !warnings.length &&
        enabledWarnings.DtfImageBackground &&
        spread.technology === PrintTechnology.DTF &&
        element.type === 'image' &&
        element.imageId &&
        galleryImage?.type !== MimeType.SVG &&
        galleryImage?.hasTransparentBackground !== true
      ) {
        const imageManipulationPluginData = getImageManipulationPluginData(
          ImageManipulationPluginTypes.BackgroundRemoval,
          element,
        );
        if (!imageManipulationPluginData?.imageModified) {
          warnings.push({
            type: WarningType.DtfImageBackground,
            uuid: element.uuid,
            spreadIndex,
            level: WarningLevel.Low,
          });
          level = WarningLevel.Low;
          text = t('dtf-technic-topbar-warning');
          tooltipLabel = t('dtf-technic-topbar-warning-tooltip');
        }
      }
    }

    return {
      warnings,
      level,
      tooltipLabel,
      text,
    };
  }, [element, galleryImage, limits, spreadIndex, enabledWarnings, hiddenWarnings, charCap, gangSheet]);

  return {
    warnings,
    level,
    tooltipLabel,
    text,
  };
}

export default useElementWarnings;
